import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showDialog, hideDialog } from '../../../core/actions';
import {
  deployAssetTemplates,
  removeAssetTemplateFromSite,
  fetchAssetTemplateOccurrences,
  fetchAssetTaskOccurrences,
  fetchAssetSiteInventories,
} from '../actions';
import { AssetRow, buildAssetRows } from '../listHelpers';

/**
 * Staging templates for a site and the two writes that follow.
 *
 * Nothing reaches the site until the summary drawer is submitted, so the cart
 * is safe to build up and abandon. Removing archives the occurrence subtree
 * rather than deleting it, which is why it asks for confirmation first.
 */
export function useAssetCart({
  intl,
  activeTemplates,
  activeGroups,
  activeItems,
  occurrenceIdByTemplateId,
}: {
  intl: any;
  activeTemplates: any[];
  activeGroups: any[];
  activeItems: any[];
  occurrenceIdByTemplateId: Map<number, number>;
}) {
  const dispatch = useDispatch();

  const [cartMode, setCartMode] = useState(false);
  const [cartTemplateIds, setCartTemplateIds] = useState<number[]>([]);
  // Starts collapsed to the edge tab; the table is what you came to read.
  const [cartOpen, setCartOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);

  const refreshSiteRows = () => {
    dispatch(fetchAssetTemplateOccurrences());
    dispatch(fetchAssetTaskOccurrences());
    dispatch(fetchAssetSiteInventories());
  };

  // Same builder the table uses, so names and areas cannot drift between them.
  const cartRows = useMemo(
    () =>
      buildAssetRows(
        activeTemplates.filter((t: any) => cartTemplateIds.includes(Number(t.id))),
        [],
        [],
        { groups: activeGroups, items: activeItems }
      ),
    [activeTemplates, cartTemplateIds, activeGroups, activeItems]
  );

  const handleToggleCartMode = () => {
    setCartMode((prev) => {
      if (prev) setCartTemplateIds([]);
      return !prev;
    });
    setCartOpen(false);
  };

  const handleToggleCart = (row: AssetRow) => {
    setCartTemplateIds((prev) =>
      prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
    );
  };

  const handleAddCartToSite = () => {
    if (cartTemplateIds.length === 0) return;
    setDeploying(true);
    // One request per template rather than a batch, so a single failure cannot
    // take the rest of the cart down with it.
    deployAssetTemplates(cartTemplateIds, (failedIds: number[]) => {
      setDeploying(false);
      setCartTemplateIds(failedIds);
      if (failedIds.length === 0) setCartMode(false);
      refreshSiteRows();
    }).forEach((action: any) => dispatch(action));
  };

  // Removing archives the occurrence subtree: open tasks go with it, completed
  // history stays. Confirmed first because it takes the site's rows away.
  const handleRemoveFromSite = (row: AssetRow) => {
    const occurrenceId = occurrenceIdByTemplateId.get(row.id);
    if (!occurrenceId) return;
    dispatch(
      showDialog('CONFIRM_DIALOG', {
        title: intl.formatMessage({ id: 'assets.cart.removeTitle' }),
        text: intl.formatMessage({ id: 'assets.cart.removeText' }, { name: row.name }),
        submitButtonText: intl.formatMessage({ id: 'assets.cart.removeConfirm' }),
        onReady: () => {
          dispatch(removeAssetTemplateFromSite(occurrenceId, refreshSiteRows));
          dispatch(hideDialog());
        },
      })
    );
  };

  return {
    cartMode,
    cartTemplateIds,
    setCartTemplateIds,
    cartOpen,
    setCartOpen,
    deploying,
    cartRows,
    handleToggleCartMode,
    handleToggleCart,
    handleAddCartToSite,
    handleRemoveFromSite,
  };
}
